import React, { createContext, useContext, useState, useEffect } from 'react';
import { authClient } from '../api/client';
import { translateApiError } from '../i18n/apiErrors';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await authClient.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (err) {
          console.error('Auth check failed:', err);
          localStorage.removeItem('access_token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [API_URL]);

  const register = async (email, password, name, role, orgnr, language) => {
    try {
      const response = await authClient.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        name,
        role,
        ...(orgnr ? { orgnr } : {}),
        ...(language ? { language } : {})
      });
      
      const { access_token, user } = response.data;
      localStorage.setItem('access_token', access_token);
      setUser(user);
      return { success: true };
    } catch (err) {
      const message = translateApiError(err.response?.data?.detail) || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authClient.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user } = response.data;
      localStorage.setItem('access_token', access_token);
      setUser(user);
      return { success: true };
    } catch (err) {
      const message = translateApiError(err.response?.data?.detail) || 'Login failed';
      setError(message);
      return { success: false, error: message, status: err.response?.status };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const response = await authClient.get(`${API_URL}/api/auth/google/start`);
      const { authorization_url } = response.data;
      window.location.href = authorization_url;
    } catch (err) {
      setError('Failed to start Google authentication');
    }
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const response = await authClient.post(
        `${API_URL}/api/auth/google/session`,
        {},
        { headers: { 'X-Session-ID': sessionId } }
      );
      
      const { access_token, user } = response.data;
      localStorage.setItem('access_token', access_token);
      setUser(user);
      return { success: true };
    } catch (err) {
      const message = translateApiError(err.response?.data?.detail) || 'Google authentication failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    try {
      const response = await authClient.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
      return response.data;
    } catch (err) {
      console.error('refreshUser failed:', err);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        register,
        login,
        loginWithGoogle,
        handleGoogleCallback,
        logout,
        refreshUser,
        setError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
